import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useLearningAssignments,
  useAssignCourse,
  useCourses,
} from "../../backend/queries/learning";
import { PageSection, EmptyState, ErrorState, LoadingState } from "../patterns";
import { Button } from "../ui/button";
import { CourseAssignmentChoices } from "./CourseAssignmentChoices";

export function UserLearningAssignments({ userId }: { userId: string }) {
  const courses = useCourses();
  const assigned = useLearningAssignments(userId);
  const assign = useAssignCourse();
  const [changes, setChanges] = useState<Record<string, boolean | null>>({});
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const published =
    courses.data?.filter((course) => course.status === "published") || [];
  const values: Record<string, boolean> = Object.fromEntries(
    (assigned.data || []).map((item) => [item.course_id, item.required]),
  );
  Object.entries(changes).forEach(([id, value]) => {
    if (value === null) delete values[id];
    else values[id] = value;
  });
  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      for (const [id, required] of Object.entries(changes)) {
        if (required === null) continue;
        await assign.mutateAsync({ id, userId, required });
        setChanges((current) => {
          const next = { ...current };
          delete next[id];
          return next;
        });
      }
      setMessage("Learning assignments saved.");
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not save assignments. Retry the remaining changes.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <PageSection
      title="Courses"
      description="Required courses must be completed before using the portal."
    >
      {courses.isPending || assigned.isPending ? (
        <LoadingState label="Loading assignments" />
      ) : courses.error || assigned.error ? (
        <ErrorState
          onRetry={() => {
            courses.refetch();
            assigned.refetch();
          }}
        />
      ) : !published.length ? (
        <EmptyState
          title="No published courses yet"
          description="Publish a course before assigning it."
          actions={
            <Button asChild variant="outline">
              <Link to="/learning">Manage learning</Link>
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4">
          <CourseAssignmentChoices
            courses={published}
            values={values}
            onChange={(id, value) =>
              setChanges((current) => ({ ...current, [id]: value }))
            }
            disabled={saving}
            lockedIds={assigned.data?.map((item) => item.course_id)}
            completedIds={assigned.data
              ?.filter((item) => item.completed_at)
              .map((item) => item.course_id)}
          />
          <Button
            className="w-fit"
            disabled={
              saving || !Object.values(changes).some((value) => value !== null)
            }
            onClick={save}
          >
            {saving ? "Saving…" : "Save assignments"}
          </Button>
        </div>
      )}
      {message && (
        <p role="status" className="mt-3 text-sm">
          {message}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      )}
    </PageSection>
  );
}
